import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Code, Database, Briefcase, Star, Users, BookOpen, MessageCircle, CheckCircle2, Trophy } from 'lucide-react';
import Button from '../components/common/Button';
import Footer from '../components/Footer';
import salaryImage from '../assets/images/salary-chart.png';
import { PLATFORM_LINKS, isAfterPaymentDeadline } from '../config/constants';

const LaunchLandingPage = () => {
  const [studentsCount, setStudentsCount] = useState(0);
  const [approvalRate, setApprovalRate] = useState(0);
  const pricingSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const duration = 5000; //  seconds
    const studentsIncrement = 5000 / (duration / 16); // 60 FPS
    const approvalIncrement = 98 / (duration / 16);
    
    const interval = setInterval(() => {
      setStudentsCount(prev => {
        if (prev < 5000) {
          return Math.min(prev + studentsIncrement, 5000);
        }
        return prev;
      });
      
      setApprovalRate(prev => {
        if (prev < 98) {
          return Math.min(prev + approvalIncrement, 98);
        }
        return prev;
      });
    }, 16);

    return () => clearInterval(interval);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToPricing = () => {
    pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const journeySteps = [
    {
      title: 'Fundamentos da Lógica e Programação',
      description: 'Base sólida para iniciar sua jornada em Java',
      icon: <Code className="w-8 h-8" />
    },
    {
      title: 'Java Core na Prática',
      description: 'Domine os conceitos essenciais com projetos reais',
      icon: <Database className="w-8 h-8" />
    },
    {
      title: 'APIs e Banco de Dados',
      description: 'Construa aplicações completas e escaláveis',
      icon: <Briefcase className="w-8 h-8" />
    },
    {
      title: 'Projetos e Preparação',
      description: 'Prepare-se para entrevistas técnicas',
      icon: <Trophy className="w-8 h-8" />
    }
  ];

  const testimonials = [
    {
      name: 'Mateus Silva',
      location: 'São Paulo',
      role: 'Dev Java Pleno',
      image: '',
      text: 'Melhor curso de todos, excelente profissional...Estou aprendendo mais com Dani do que na própria faculdade. Que Deus continue te abençoando. Só tenho agradece!'
    },
    {
      name: 'Dércio',
      location: 'Moçambique',
      role: 'Dev Backend Java',
      image: '',
      text: 'Gostava de agradecer pelas aulas de Java que tivemos na ultima semana, sou de Moçambique e aqui o Spring Boot é muito usado nas empresas, assim como bancos principalmente. O curso me ajudou a alavancar minha carreira. Obrigada Dani '
    },
    {
      name: 'Elena',
      location: 'Belo Horizonte',
      role: 'Dev Java Júnior',
      image: '',
      text: 'Sou iniciante na programação e sinto que com a Dani comecei a aprender a programar de uma maneira completa. Com ela, comecei a entender, desde o início, sobre a importância do planejamento, da organização, do levantamento de requisitos, modelagem do banco, da arquitetura limpa, dos testes e também a importância de documentar minha API.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-black to-[#1a1a1a] z-0" />
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full bg-[url('https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg')] bg-cover bg-center" />
        </div>
        
        <div className="container mx-auto px-4 py-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-[#f2055c] to-white bg-clip-text text-transparent">
                Do Zero ao Dev Java Disputado(a) em 6 meses!
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-gray-300">
              Uma formação online, uma trilha validada, projetos reais e suporte completo para sua transformação profissional.
            </p>
            {/* <Button size="large" onClick={handleScrollToTop}>
              <span className="flex items-center gap-2">
                Quero garantir minha vaga!
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button> */}
          </div>
        </div>
      </section>

      {/* Instructor Authority */}
      <section className="py-20 bg-gradient-to-br from-[#0d0d0d] to-black">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2">
              <div className="relative">
                <div className="w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-[#f2055c] shadow-xl mx-auto">
                  <img 
                    src="https://static-media.hotmart.com/bA9jLWmkRQu7lOmYuDWTlfh9riA=/filters:quality(100):format(webp)/klickart-prod/uploads/media/file/8242028/dani-whats.jpeg"
                    alt="Instrutora Daniele"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 right-0 md:right-12 bg-[#f2055c] text-white px-6 py-3 rounded-full shadow-lg">
                  <span className="font-bold">17 anos com Java</span>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/2 space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Aprenda com quem vive de código há mais de 17 anos
              </h2>
              <p className="text-xl text-gray-300">
                Formando devs prontos para o mercado real, com experiência em grandes empresas e projetos internacionais.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-3xl font-bold text-[#f2055c]">
                    {Math.round(studentsCount)}+
                  </div>
                  <div className="text-gray-400">Alunos formados</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-3xl font-bold text-[#f2055c]">
                    {Math.round(approvalRate)}%
                  </div>
                  <div className="text-gray-400">Taxa de aprovação</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Salary Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Quanto ganha um <span className="border-b-4 border-[#f2055c]">Dev Java</span> no Brasil?
          </h2>
          <div className="max-w-4xl mx-auto">
            <img 
              src={salaryImage}
              alt="Salários de Desenvolvedores Java"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Detailed Salary Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block">
                <h2 className="text-3xl md:text-4xl font-bold mb-2">
                  <span className="relative">
                    Devs Java Ganham 
                    <span className="relative">
                      <span className="text-[#f2055c]"> mais!</span>
                      <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 100 6" preserveAspectRatio="none">
                        <path d="M0,5 Q25,0 50,5 T100,5" stroke="#f2055c" strokeWidth="2" fill="none" />
                      </svg>
                    </span>
                  </span>
                </h2>
              </div>
              <div className="max-w-3xl mx-auto mt-8">
                <p className="text-xl text-gray-300 mb-4 relative">
                  <span className="absolute -left-4 top-0 text-[#f2055c] font-bold text-2xl">"</span>
                  Segundo pesquisa Código Fonte TV de 2024, realizada com 
                  <span className="font-bold text-white"> 15.049 profissionais de TI</span>, 
                  desenvolvedores Java possuem um dos salários mais altos do mercado.
                  <span className="absolute -right-4 bottom-0 text-[#f2055c] font-bold text-2xl">"</span>
                </p>
              </div>
            </div>

            {/* Language Salary Comparison */}
            <div className="bg-white/5 p-8 rounded-lg mb-12">
              <h3 className="text-2xl font-bold mb-8 text-center">
                Média Mensal de Desenvolvedores por Linguagem
              </h3>
              <div className="space-y-4">
                {[
                  { lang: 'JAVA', salary: 9639.04, width: '100%' },
                  { lang: 'C#', salary: 9093.60, width: '94%' },
                  { lang: 'TypeScript', salary: 8896.22, width: '92%' },
                  { lang: 'Python', salary: 8779.64, width: '91%' },
                  { lang: 'Dart', salary: 8766.52, width: '91%' },
                  { lang: 'JavaScript', salary: 8021.90, width: '83%' },
                  { lang: 'PHP', salary: 7604.55, width: '79%' }
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{item.lang}</span>
                      <span className="text-[#05f217]">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(item.salary)}
                      </span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full">
                      <div 
                        className="h-full bg-[#05f217] rounded-full"
                        style={{ width: item.width }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Senior Levels Comparison */}
            <div className="bg-white/5 p-8 rounded-lg mb-12">
              <h3 className="text-2xl font-bold mb-8 text-center">
                Sêniores ganham ainda mais!
              </h3>
              <div className="space-y-6">
                {[
                  { level: 'Java Especialista', salary: 19304.59, highlight: true },
                  { level: 'Java Sênior', salary: 14301.81, highlight: false }
                ].map((item, index) => (
                  <div key={index} className={`p-6 rounded-lg ${item.highlight ? 'bg-[#05f217]/10 border border-[#05f217]/20' : 'bg-white/5'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-medium">{item.level}</span>
                      <span className={`text-2xl font-bold ${item.highlight ? 'text-[#05f217]' : 'text-white'}`}>
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(item.salary)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center space-y-6">
              <div className="bg-gradient-to-r from-black via-[#05f217]/10 to-black p-8 rounded-lg border border-[#05f217]/20">
                <p className="text-2xl md:text-3xl font-bold mb-2">
                  <span className="text-white">E detalhe: o especialista passa dos</span>
                  <br className="hidden md:block" />
                  <span className="text-[#05f217] text-4xl md:text-5xl"> R$ 19.000</span>
                </p>
                <p className="text-xl md:text-2xl mt-4">
                  <span className="text-white">é isso que </span>
                  <span className="relative inline-block">
                    <span className="text-[#05f217]">você vai conseguir!</span>
                    <svg className="absolute -bottom-1 left-0 w-full" height="4" viewBox="0 0 100 4" preserveAspectRatio="none">
                      <path d="M0,2 Q25,0 50,2 T100,2" stroke="#05f217" strokeWidth="2" fill="none" />
                    </svg>
                  </span>
                </p>
              </div>
              <p className="text-sm text-gray-400">
                Fonte: Código Fonte (2024)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Steps */}
      <section className="py-20 bg-gradient-to-br from-[#0d0d0d] to-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Sua jornada para o sucesso
          </h2>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  number: '1',
                  title: 'Planejamento',
                  description: 'Base inicial para sua jornada',
                  icon: <Star className="w-8 h-8" />,
                  color: '#f2055c',
                  highlight: true
                },
                {
                  number: '2',
                  title: 'Introdução à Programação',
                  icon: <Code className="w-8 h-8" />,
                  color: '#f2055c'
                },
                {
                  number: '3',
                  title: 'Fundamentos do Java',
                  icon: <Database className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '4',
                  title: 'Programação Orientada a Objetos (POO)',
                  icon: <Briefcase className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '5',
                  title: 'Estruturas de Dados e Coleções',
                  icon: <BookOpen className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '6',
                  title: 'Exceções',
                  icon: <MessageCircle className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '7',
                  title: 'Entrada e Saída I/O',
                  icon: <Star className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '8',
                  title: 'Java avançado (Thread / Lambda)',
                  icon: <Users className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '9',
                  title: 'Estrutura de Dados (Busca e Ordenação)',
                  icon: <Database className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '10',
                  title: 'Java Web - Servlet',
                  icon: <Code className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '11',
                  title: 'Spring Boot - Primeiros passos',
                  icon: <Star className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '12',
                  title: 'Testes',
                  icon: <CheckCircle2 className="w-8 h-8" />,
                  color: '#05f217'
                },
                {
                  number: '13',
                  title: 'Deploy',
                  icon: <Trophy className="w-8 h-8" />,
                  color: '#05f217'
                }
              ].map((module, index) => (
                <div 
                  key={index} 
                  className={`relative bg-white/5 p-6 rounded-lg border ${
                    module.highlight 
                      ? 'border-[#f2055c]/50 bg-[#f2055c]/5' 
                      : 'border-white/10 hover:border-[#f2055c]/50'
                  } transition-all group`}
                >
                  <div className={`absolute -top-4 -left-4 w-8 h-8 rounded-full ${
                    module.highlight 
                      ? 'bg-[#f2055c] scale-125' 
                      : 'bg-[#f2055c]'
                  } text-black font-bold flex items-center justify-center text-sm`}>
                    {module.number}
                  </div>
                  <div className="mb-4 text-[#f2055c] opacity-80 group-hover:opacity-100 transition-opacity">
                    {module.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-[#f2055c] transition-colors">
                    {module.title}
                  </h3>
                  {module.description && (
                    <p className="text-gray-400 mb-3">{module.description}</p>
                  )}
                  <div className={`h-1 w-12 ${
                    module.highlight 
                      ? 'w-full bg-[#f2055c]/50' 
                      : 'bg-[#f2055c]/30 group-hover:w-full'
                  } transition-all duration-300`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Promise Section */}
      <section className="py-20 bg-black relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg')] bg-cover bg-center" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-transparent" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Main Promise */}
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="relative inline-block">
                  Minha promessa é:
                  <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 100 6" preserveAspectRatio="none">
                    <path d="M0,5 Q25,0 50,5 T100,5" stroke="#f2055c" strokeWidth="2" fill="none" />
                  </svg>
                </span>
                <br />
                <br />
                <span className="text-[#05f217]">DO ZERO </span><span className="text-[#ffffff]">AO DEV JAVA DISPUTADO</span><span className="text-[#05f217]"> EM 6 MESES</span>
              </h2>
            </div>

            {/* Warning Box */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-8 mb-16">
              <div className="flex items-start gap-4">
                <div className="text-[#f2055c] text-2xl">⚠️</div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Mas atenção!</h3>
                  <p className="text-xl text-gray-300 mb-4">
                    Eu não estou prometendo que você vai sair do zero para senior ganhando R$ 15.000 em 6 meses.
                  </p>
                  <p className="text-xl text-[#f2055c] font-bold">
                    Isso seria MENTIRA, e eu não minto para vocês.
                  </p>
                </div>
              </div>
            </div>

            {/* Real Promise */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold mb-8 text-center">
                O que eu prometo é <span className="text-[#f2055c]">REAL</span> e <span className="text-[#f2055c]">HONESTO</span>:
              </h3>
              
              <div className="space-y-6">
                {[
                  {
                    from: 'ZERO',
                    to: 'ESTAGIÁRIO DISPUTADO',
                    icon: '🎯',
                    color: '#f2055c'
                  },
                  {
                    from: 'ESTAGIÁRIO',
                    to: 'JÚNIOR DISPUTADO',
                    icon: '🚀',
                    color: '#05f217'
                  },
                  {
                    from: 'JÚNIOR',
                    to: 'PLENO DISPUTADO',
                    icon: '💫',
                    color: '#f2055c'
                  }
                ].map((step, index) => (
                  <div 
                    key={index}
                    className="bg-white/5 border border-white/10 rounded-lg p-6 relative overflow-hidden group hover:border-[#f2055c]/50 transition-all duration-300"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                      <div className="text-4xl">{step.icon}</div>
                      <div className="flex-1 text-center md:text-left">
                        <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
                          <span className="text-gray-400">Se você está no</span>
                          <span className="font-bold text-xl">{step.from}</span>
                          <span className="text-gray-400">Em 6 meses será um</span>
                          <span className="font-bold text-xl text-[#f2055c]">{step.to}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Final Message */}
            <div className="text-center space-y-6">
              <h3 className="text-2xl font-bold">
                Entenderam a diferença?
              </h3>
              <p className="text-lg md:text-xl text-gray-300">
                Cada nível tem seu tempo de evolução. Mas o que eu garanto é que em 6 meses você vai <span className="text-[#f2055c] font-bold">SUBIR DE NÍVEL</span> e se tornar <span className="text-[#f2055c] font-bold">DISPUTADO</span> nesse novo nível.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={pricingSectionRef} className="py-20 bg-gradient-to-br from-[#0d0d0d] to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="relative inline-block">
                  O Seu Futuro
                  <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 100 6" preserveAspectRatio="none">
                    <path d="M0,5 Q25,0 50,5 T100,5" stroke="#f2055c" strokeWidth="2" fill="none" />
                  </svg>
                </span>
                <br />
                <span className="text-[#f2055c]">Começa Agora!</span>
              </h2>
              <p className="text-xl text-gray-400 mt-6">
                Invista em você e transforme sua carreira em 6 meses
              </p>
            </div>
            <div className="bg-white/5 p-8 rounded-lg border border-white/10">
              <div className="text-gray-400 line-through text-2xl mb-2">R$ 2.997</div>
              <div className="text-4xl md:text-5xl font-bold text-[#f2055c] mb-4">
                12x de R$ 72,09
              </div>
              <div className="text-xl mb-8">ou R$ 697 à vista</div>
              
              <div className="space-y-4 mb-8">
                {[
                  'Acesso vitalício ao curso',
                  'Comunidade exclusiva',
                  'Certificado reconhecido',
                  'Suporte direto com a Daniele'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center justify-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#f2055c]" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="large" 
                fullWidth 
                href={isAfterPaymentDeadline() ? PLATFORM_LINKS.HOTMART_PAYMENT_AFTER_DEADLINE : PLATFORM_LINKS.HOTMART_PAYMENT}
              >
                <span className="flex items-center justify-center gap-2">
                  Finalizar Compra!
                </span>
              </Button>

              <p className="mt-4 text-sm text-gray-400">
                Garantia incondicional de 7 dias
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white text-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Histórias reais de transformação
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  {/* <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  /> */}
                  <div>
                    <h3 className="font-bold">{testimonial.name}</h3>
                    <p className="text-[#f2055c]">{testimonial.role}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
              <Button size="large" onClick={handleScrollToPricing}>
                <span className="flex items-center gap-2">
                  Quero começar minha jornada agora!
                </span>
              </Button>
            </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white text-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Perguntas Frequentes
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                q: "Preciso ter experiência prévia?",
                a: "Não, o curso começa do absoluto zero. Minha metodologia foi desenvolvida para quem não tem nenhum conhecimento se tornar um especialista Java."
              },
              {
                q: "O curso é atualizado?",
                a: "Sim, o conteúdo é constantemente atualizado com as últimas versões e melhores práticas."
              },
              {
                q: "Posso assistir quando quiser?",
                a: "Sim, você tem acesso vitalício ao conteúdo e pode assistir no seu próprio ritmo."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-lg mb-2">{faq.q}</h3>
                <p className="text-gray-700">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0d0d0d] to-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Cada mês que você espera é um mês preso no mesmo lugar...
          </h2>
          <p className="text-xl mb-8 text-gray-400">
            Junte-se a milhares de devs que já transformaram suas carreiras com a Jornada Java
          </p>
          <Button size="large" onClick={handleScrollToPricing}>
            <span className="flex items-center gap-2">
              Sim, quero entrar na Jornada Java! 🚀
              <ArrowRight className="w-5 h-5" />
            </span>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LaunchLandingPage;